import { findNearestElement, type Direction } from './findNearestElement';

export const focusableClassName = 'tv-focusable';
export const focusGroupClassName = 'tv-focus-group';

const focusSelector = `.${focusableClassName}`;
const scrollMargin = 24;

const isAvailable = (element: HTMLElement): boolean => {
  if (
    !document.documentElement.contains(element) ||
    element.hasAttribute('disabled') ||
    element.getAttribute('aria-disabled') === 'true' ||
    element.getAttribute('aria-hidden') === 'true'
  ) {
    return false;
  }

  const style = window.getComputedStyle(element);
  const rect = element.getBoundingClientRect();
  return (
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    rect.width > 0 &&
    rect.height > 0
  );
};

const getFocusableElements = (root: ParentNode = document): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>(focusSelector)).filter(
    isAvailable,
  );

const getScrollableAncestors = (element: HTMLElement): HTMLElement[] => {
  const ancestors: HTMLElement[] = [];
  let parent = element.parentElement;

  while (parent && parent !== document.documentElement) {
    if (
      parent.scrollHeight > parent.clientHeight + 1 ||
      parent.scrollWidth > parent.clientWidth + 1
    ) {
      ancestors.push(parent);
    }
    parent = parent.parentElement;
  }

  return ancestors;
};

const scrollIntoNearestView = (
  element: HTMLElement,
  ancestors: HTMLElement[],
): void => {
  ancestors.forEach((ancestor) => {
    const elementRect = element.getBoundingClientRect();
    const ancestorRect = ancestor.getBoundingClientRect();
    const topEdge = ancestorRect.top + scrollMargin;
    const bottomEdge = ancestorRect.bottom - scrollMargin;
    const leftEdge = ancestorRect.left + scrollMargin;
    const rightEdge = ancestorRect.right - scrollMargin;

    if (elementRect.top < topEdge) {
      ancestor.scrollTop -= topEdge - elementRect.top;
    } else if (elementRect.bottom > bottomEdge) {
      ancestor.scrollTop += elementRect.bottom - bottomEdge;
    }

    if (elementRect.left < leftEdge) {
      ancestor.scrollLeft -= leftEdge - elementRect.left;
    } else if (elementRect.right > rightEdge) {
      ancestor.scrollLeft += elementRect.right - rightEdge;
    }
  });
};

const focusWithoutBrowserJump = (element: HTMLElement): void => {
  const ancestors = getScrollableAncestors(element);
  const positions = ancestors.map((ancestor) => ({
    ancestor,
    left: ancestor.scrollLeft,
    top: ancestor.scrollTop,
  }));
  const windowX = window.pageXOffset;
  const windowY = window.pageYOffset;

  element.focus();

  positions.forEach(({ ancestor, left, top }) => {
    ancestor.scrollLeft = left;
    ancestor.scrollTop = top;
  });
  window.scrollTo(windowX, windowY);
  scrollIntoNearestView(element, ancestors);
};

class FocusManager {
  private currentFocus: HTMLElement | null = null;
  private currentScope = '';
  private readonly rememberedFocus = new Map<string, string>();

  setScope(scope: string): void {
    if (scope === this.currentScope) return;
    this.currentScope = scope;
    this.currentFocus = null;
  }

  track(target: EventTarget | null): void {
    const element =
      target instanceof Element
        ? target.closest<HTMLElement>(focusSelector)
        : null;
    if (!element || !isAvailable(element)) return;

    this.currentFocus = element;
    const focusKey = element.dataset.focusKey;
    if (focusKey && this.currentScope) {
      this.rememberedFocus.set(this.currentScope, focusKey);
    }
  }

  focus(element: HTMLElement | null): boolean {
    if (!element || !isAvailable(element)) return false;

    focusWithoutBrowserJump(element);
    this.track(element);
    return true;
  }

  focusFirst(root: ParentNode = document): boolean {
    const elements = getFocusableElements(root);
    const preferred = elements.find(
      (element) => element.dataset.focusPreferred === 'true',
    );
    return this.focus(preferred || elements[0] || null);
  }

  restore(root: ParentNode = document): boolean {
    const focusKey = this.rememberedFocus.get(this.currentScope);
    if (!focusKey) return false;

    const element = getFocusableElements(root).find(
      (candidate) => candidate.dataset.focusKey === focusKey,
    );
    return this.focus(element || null);
  }

  ensureFocus(root: ParentNode = document): boolean {
    if (this.getCurrentFocus()) return true;
    return this.restore(root) || this.focusFirst(root);
  }

  select(): void {
    this.getCurrentFocus()?.click();
  }

  move(direction: Direction): void {
    const focused = this.getCurrentFocus();
    if (!focused) {
      if (!this.restore()) this.focusFirst();
      return;
    }

    const allElements = getFocusableElements();
    const group = focused.closest<HTMLElement>(`.${focusGroupClassName}`);
    const groupElements = group
      ? allElements.filter(
          (element) =>
            element.closest<HTMLElement>(`.${focusGroupClassName}`) === group,
        )
      : [];
    const withinGroup = findNearestElement({
      currentElement: focused,
      direction,
      elements: groupElements,
    });

    if (this.focus(withinGroup)) return;

    const outsideGroup = group
      ? allElements.filter(
          (element) =>
            element.closest<HTMLElement>(`.${focusGroupClassName}`) !== group,
        )
      : allElements;

    this.focus(
      findNearestElement({
        currentElement: focused,
        direction,
        elements: outsideGroup,
      }),
    );
  }

  private getCurrentFocus(): HTMLElement | null {
    const activeElement =
      document.activeElement instanceof Element
        ? document.activeElement.closest<HTMLElement>(focusSelector)
        : null;

    if (activeElement && isAvailable(activeElement)) {
      this.currentFocus = activeElement;
      return activeElement;
    }
    if (this.currentFocus && isAvailable(this.currentFocus)) {
      return this.currentFocus;
    }
    this.currentFocus = null;
    return null;
  }
}

export const focusManager = new FocusManager();
